import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import AuthenticationService from "service/auth/AuthenticationService";
import EventService from "service/event/EventService";
import "../../styles/progressNotes.scss";
import PatientProgressNotesService from "../../service/PatientProgressNotesService";
import { TAG_ALL, noteAuthor, tagName, tagValue, toCard } from "../../helper/progressNoteFormatters";
import { CATEGORIES } from "./data";
import NoteCard from "./NoteCard";
import ComposerModal from "./ComposerModal";

/**
 * Progress notes — the feed (section Content column). Source: Figma Progress
 * notes list board (2156:3257, ALL filter) and the filtered variant (2159:3257,
 * MEDICAL_RECORDS active). A Slack-style activity feed inside a single card:
 * header ("Progress Notes" + purple "+ Add new"), a full-width Search box, a
 * wrapping single-select category filter-chip row, a divider, the mapped
 * NoteCard list, and a footer bar ("Showing N of T" / "Show more").
 *
 * This is NOT the sec-card table + editor archetype — it is a bespoke feed. The
 * composer is a full-screen portal modal (ComposerModal) opened by "+ Add new",
 * so the section registers as contentOnly (no editor rail). A card's reply
 * control opens that SAME modal in comment mode (`replyingTo`) — Figma gives a
 * comment the full editor + Tasks panel, not an inline box.
 *
 * Live against the Eve progress_notes API (PatientProgressNotesService). Reading
 * is wired here — chips, search and paging all hit the server — and so are the
 * per-card writes: acknowledge, and the comment's two-call POST+PATCH (the
 * handlers below; the composer builds the body and hands it down). Creating a
 * note, and either path's Zoho tasks, is ComposerModal's.
 */

/** Bridge's numberOfProgressNotesTofetch — a page of the feed. */
const PAGE_SIZE = 15;

/**
 * The server's own rejection when an employee acknowledges twice, verbatim from
 * bridge so ops read the same sentence in both apps. The cards already refuse a
 * second like, so in practice this surfaces the race: the note was acknowledged
 * from another tab (or by this employee's earlier session) and the card on
 * screen predates it.
 */
const ALREADY_ACKNOWLEDGED = "You have already acknowledged it.";

const NO_IDENTITY =
  "Could not identify the signed-in employee — please sign in again.";

/** The "no filter" pseudo-chip. Named off the tag table so it can't drift. */
const ALL = tagName(TAG_ALL);

const SearchIcon = () => (
  <svg className="pn-search-icon" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="5.25" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 12l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/**
 * The single place that decides WHICH endpoint a given feed state reads from, so
 * the first-page effect and "Show more" can never disagree about it.
 *
 * Search wins over the chip because the search endpoint has no tag slot at all
 * (APIConfig.progressNotes.search only substitutes patientId + text) — see the
 * chip reset in the debounce below.
 */
const fetchPage = (service, patientId, query, page, onResponse, onError) => {
  if (query.search) {
    return service.search(patientId, query.search, onResponse, onError);
  }
  if (query.category === ALL) {
    return service.get(patientId, page, PAGE_SIZE, onResponse, onError);
  }
  return service.getWithType(
    patientId,
    page,
    PAGE_SIZE,
    tagValue(query.category),
    onResponse,
    onError
  );
};

const ProgressNotes = ({ patient }) => {
  const [composerOpen, setComposerOpen] = useState(false);

  /**
   * The card being replied to, or null. Holds the CARD rather than just its id
   * because the composer quotes the parent's rendered body back at the author —
   * and the card is the only shape whose `html` is already sanitised.
   */
  const [replyTo, setReplyTo] = useState(null);

  /**
   * Single source of server state, like AppointmentsDashboard's `filters`: the
   * chip and the (debounced) search text are the only things that pick a query,
   * and they must change atomically — a search that reset the chip in a second
   * setState would fire a second, contradictory request first.
   */
  const [query, setQuery] = useState({ category: ALL, search: "" });

  // What the user is typing, before the 300ms debounce promotes it into `query`.
  const [searchInput, setSearchInput] = useState("");

  const [cards, setCards] = useState([]);
  const [total, setTotal] = useState(0);
  // The last page successfully loaded. Only ever advanced by a successful
  // "Show more", so a failed one retries the same page instead of skipping it.
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // A short page means the server has nothing after it, whatever meta.total
  // claims — belt and braces against an endless "Show more".
  const [exhausted, setExhausted] = useState(false);
  // Bumped after a write (the composer's note, or a comment/acknowledgement from
  // a card) to re-pull page 1. A write can change both the list and meta.total,
  // and a new note lands at the top (the feed is sorted _id desc), so refetching
  // is simpler and more honest than splicing the local array and guessing the
  // server's ordering.
  const [reloadKey, setReloadKey] = useState(0);

  const service = useRef(new PatientProgressNotesService()).current;
  const auth = useRef(new AuthenticationService()).current;
  const patientId = patient && patient.id;

  // The write callbacks below settle long after the click, and the section can
  // be unmounted in between (the shell swaps sections freely), so each one
  // checks this before refreshing or reporting.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  /**
   * Bumped whenever the query changes or the section unmounts (the effect
   * cleanup below). "Show more" captures it and drops its response if it no
   * longer matches — otherwise a slow page-2 could append itself onto the list
   * of a filter the user has since switched away from.
   */
  const generation = useRef(0);

  const showError = useCallback((message) => {
    EventService.emit("showError", { message });
  }, []);

  /**
   * Promote the typed text into the query 300ms after the last keystroke, so a
   * word typed at speed costs one request rather than one per character.
   *
   * Searching also forces the chip back to ALL. This is correctness, not polish:
   * the search endpoint ignores the tag, so a chip left active would label
   * results that flatly contradict it. Bridge does the same.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      const text = searchInput.trim();
      setQuery((prev) =>
        // Returning prev makes React bail out — no re-render, no refetch. Needed
        // because this timer also runs on mount and after a chip clears the box.
        prev.search === text
          ? prev
          : { category: text ? ALL : prev.category, search: text }
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  /**
   * First page of whatever the current query is. Replaces the list — every other
   * page is appended by loadMore.
   */
  useEffect(() => {
    if (!patientId) return undefined;

    let cancelled = false;
    setLoading(true);

    fetchPage(
      service,
      patientId,
      query,
      1,
      (res) => {
        if (cancelled) return;
        const items = (res && res.items) || [];
        setCards(items.map(toCard));
        setTotal(res && res.meta && typeof res.meta.total === "number" ? res.meta.total : items.length);
        setPage(1);
        setExhausted(items.length < PAGE_SIZE);
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        setCards([]);
        setTotal(0);
        setExhausted(true);
        setLoading(false);
        showError(`Could not load progress notes: ${(err && err.message) || ""}`);
      }
    );

    return () => {
      cancelled = true;
      generation.current += 1;
    };
  }, [patientId, query, service, showError, reloadKey]);

  /** Append the next page to the list rather than replacing it. */
  const loadMore = () => {
    if (loadingMore) return;
    const next = page + 1;
    const gen = generation.current;
    setLoadingMore(true);

    fetchPage(
      service,
      patientId,
      query,
      next,
      (res) => {
        if (generation.current !== gen) return;
        const items = (res && res.items) || [];
        // Dedupe on _id: the feed is newest-first over a live collection, so a
        // note written between page 1 and page 2 shifts every later page down
        // and would re-serve a note already on screen.
        setCards((prev) => {
          const seen = new Set(prev.map((c) => c.id));
          return prev.concat(items.map(toCard).filter((c) => !seen.has(c.id)));
        });
        if (res && res.meta && typeof res.meta.total === "number") setTotal(res.meta.total);
        setPage(next);
        setExhausted(items.length < PAGE_SIZE);
        setLoadingMore(false);
      },
      (err) => {
        if (generation.current !== gen) return;
        setLoadingMore(false);
        showError(`Could not load more progress notes: ${(err && err.message) || ""}`);
      }
    );
  };

  /* ---------------------------------------------------------------------------
   * Writes. Comments and acknowledgements are the two things a card can do.
   *
   * Each returns a PROMISE so the card can disable its own control while the
   * request is in flight; none of them hand back server data, because the card
   * never renders a local guess — every success ends in a refetch and the counts
   * on screen are always the ones the server just sent.
   * ------------------------------------------------------------------------- */

  /**
   * Who is writing — one shared derivation, `noteAuthor`, for every author
   * block this feature sends.
   *
   * It used to read employeeProfile only, on the premise that the operator
   * always signs in as an employee. A DOCTOR SESSION HAS NONE — phr's
   * /doclogin nulls it outright, and bridge defines a doctor login as
   * `doctorProfile.id > 0 && !employeeProfile` — so this returned null, the
   * author block went out without an id, and the server answered 422
   * `{"author": {"employeeId": "required field"}}` for every comment and
   * acknowledge. The doctor id belongs in `employeeId` — see noteAuthor.
   *
   * Still returns null when there is no id at all, so a write is not attempted
   * with an anonymous author.
   */
  const identify = useCallback(() => {
    const me = noteAuthor(auth.getUserProfile());
    return me.employeeId ? me : null;
  }, [auth]);

  /**
   * Refresh ONE card from the server after a write to it, and splice it back in
   * place. The counts on screen stay the server's — but the list, the page the
   * user has paged to, and their scroll position all survive.
   *
   * Re-pulling page 1 (what `reloadKey` does) is right for a NEW note, which
   * lands at the top anyway. It is wrong here: acknowledging a note the user
   * paged three deep to would collapse the feed back to the first 15 and scroll
   * the note they just acted on out of view.
   */
  const refreshNote = useCallback(
    (noteId) =>
      new Promise((resolve) => {
        service.getOne(
          noteId,
          (note) => {
            if (mounted.current && note && note._id) {
              setCards((prev) => prev.map((c) => (c.id === noteId ? toCard(note) : c)));
            }
            resolve();
          },
          // The write already succeeded; failing to re-read it is not worth an
          // error toast. The card keeps its pre-write counts until the next load.
          () => resolve()
        );
      }),
    [service]
  );

  /**
   * Add a comment. TWO calls, because a comment carries no note id of its own:
   * create it, then link it.
   *
   * `body` arrives FINISHED — escaped and task-spliced by ComposerModal, which is
   * the only place that knows the typed text. Do not run it through toWireBody
   * again: a second pass would escape the first pass's own entities ("&lt;" ->
   * "&amp;lt;") and the task block's <ol> markup, and the feed would render a
   * comment full of visible tags.
   *
   * The link PATCH sends ONLY the new id — `{comments: [id]}` — because the
   * server APPENDS to the note's array rather than replacing it. That is
   * verified, not assumed: a live note holds 7 comments while bridge has only
   * ever PATCHed one id at a time. Sending the merged array would be a different
   * operation, and one that races — it would resurrect any comment deleted since
   * this page loaded, and drop any added by someone else in the meantime.
   */
  const handleComment = useCallback(
    (noteId, body) => {
      const author = identify();
      if (!author) {
        showError(NO_IDENTITY);
        return Promise.reject(new Error(NO_IDENTITY));
      }

      return new Promise((resolve, reject) => {
        service.createComment(
          { body, author, acknowledgers: [] },
          (created) => {
            const commentId = created && created._id;
            if (!commentId) {
              // A 2xx with no id means we cannot link it, and retrying would
              // create a second one. Stop and say so.
              showError("The comment was saved but the server returned no id for it.");
              reject(new Error("createComment returned no _id"));
              return;
            }

            service.updateProgressNote(
              noteId,
              { comments: [commentId] },
              () => {
                refreshNote(noteId).then(resolve);
              },
              (err) => {
                // Step 1 landed, step 2 did not: the comment exists but hangs off
                // no note. Say exactly that — reporting success would leave the
                // clinician believing a comment is on the record when no reader
                // will ever see it, and a blind retry would orphan another one.
                showError(
                  `Your comment was saved but could not be attached to the note, so it will not appear in the feed. ${
                    (err && err.message) || ""
                  }`
                );
                reject(err || new Error("Could not link the comment"));
              }
            );
          },
          (err) => {
            showError(`Could not post the comment: ${(err && err.message) || ""}`);
            reject(err || new Error("Could not post the comment"));
          }
        );
      });
    },
    [identify, refreshNote, service, showError]
  );

  /**
   * Acknowledge a note. Same append semantics as the comment link above — only
   * the new acknowledger goes on the wire, never the merged list.
   */
  const handleLikeNote = useCallback(
    (noteId) => {
      const me = identify();
      if (!me) {
        showError(NO_IDENTITY);
        return Promise.reject(new Error(NO_IDENTITY));
      }

      return new Promise((resolve, reject) => {
        service.updateProgressNote(
          noteId,
          { acknowledgers: [me] },
          () => {
            refreshNote(noteId).then(resolve);
          },
          (err) => {
            // The duplicate rejection is the only failure this can realistically
            // hit — the card refuses a second like, so getting here means the
            // note was acknowledged elsewhere and this card is stale. Bridge's
            // wording, and a refresh so the card stops offering the action.
            showError(ALREADY_ACKNOWLEDGED);
            refreshNote(noteId);
            reject(err || new Error(ALREADY_ACKNOWLEDGED));
          }
        );
      });
    },
    [identify, refreshNote, service, showError]
  );

  /** Acknowledge a comment. Comments carry their own acknowledgers. */
  const handleLikeComment = useCallback(
    (noteId, commentId) => {
      const me = identify();
      if (!me) {
        showError(NO_IDENTITY);
        return Promise.reject(new Error(NO_IDENTITY));
      }

      return new Promise((resolve, reject) => {
        service.updateComment(
          commentId,
          { acknowledgers: [me] },
          () => {
            refreshNote(noteId).then(resolve);
          },
          (err) => {
            showError(ALREADY_ACKNOWLEDGED);
            refreshNote(noteId);
            reject(err || new Error(ALREADY_ACKNOWLEDGED));
          }
        );
      });
    },
    [identify, refreshNote, service, showError]
  );

  /**
   * A chip drops any active search: the search endpoint can't honour a tag, so
   * the only way a chip click can actually take effect is to leave search mode.
   * (The mirror of the reset in the debounce above — the two states are never
   * both live.)
   */
  const selectCategory = (category) => {
    // Re-clicking the chip that is already showing would refetch the same page
    // for nothing (a fresh object identity always retriggers the effect).
    if (query.category === category && !query.search) return;
    setSearchInput("");
    setQuery({ category, search: "" });
  };

  // No patient id yet (PatientDetails is still resolving the record) — show the
  // feed chrome and hold, rather than firing a request for `undefined`.
  const busy = loading || !patientId;

  // Never offer "Show more" while searching: that endpoint ignores paging
  // entirely, so page+1 would re-serve the same rows.
  const canShowMore = !busy && !query.search && !exhausted && cards.length < total;

  // Lets every card spot this employee's own acknowledgement in the list the
  // note already carries — no extra request, and no "did I like this?" state to
  // keep in sync with the server.
  const me = identify();
  const meId = me ? me.employeeId : undefined;

  return (
    <div className="pn-feed">
      <header className="pn-head">
        <h2 className="pn-title">Progress Notes</h2>
        <button type="button" className="pn-add-new" onClick={() => setComposerOpen(true)}>
          <span className="pn-add-plus" aria-hidden="true">
            +
          </span>{" "}
          Add new
        </button>
      </header>

      <div className="pn-search">
        <input
          type="text"
          className="pn-search-input"
          placeholder="Search"
          aria-label="Search notes"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <SearchIcon />
      </div>

      <div className="pn-chips">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            className={`pn-chip${cat === query.category ? " is-active" : ""}`}
            aria-pressed={cat === query.category}
            onClick={() => selectCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="pn-divider" />

      <div className="pn-list" aria-busy={busy}>
        {busy && cards.length === 0 ? <span className="pn-showing">Loading notes…</span> : null}

        {!busy && cards.length === 0 ? (
          <span className="pn-showing">
            {query.search ? "No notes match this search." : "No progress notes yet."}
          </span>
        ) : null}

        {/* Newest-first is the server's sort=[("_id",-1)] — do not re-sort. */}
        {cards.map((card) => (
          <NoteCard
            key={card.id}
            card={card}
            meId={meId}
            onReply={setReplyTo}
            onLikeNote={handleLikeNote}
            onLikeComment={handleLikeComment}
          />
        ))}
      </div>

      <footer className="pn-foot">
        <span className="pn-showing">
          Showing {cards.length} of {total}
        </span>
        {canShowMore ? (
          <button type="button" className="pn-showmore" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "Loading…" : "Show more"}
          </button>
        ) : null}
      </footer>

      {composerOpen ? (
        <ComposerModal
          patient={patient}
          /* The chip the feed is filtered by opens as the composer's tab. ALL
             is the pseudo-chip and seeds nothing — the composer guards it. */
          initialCategory={query.category}
          onCreated={() => setReloadKey((k) => k + 1)}
          onClose={() => setComposerOpen(false)}
        />
      ) : null}

      {/*
        The same modal in comment mode. No `onCreated` on purpose: handleComment
        already ends in refreshNote(noteId), which re-reads just this card. The
        reloadKey bump the note composer uses would be wrong here — it re-pulls
        page 1, and a comment on a note the user paged three deep to would
        collapse the feed and scroll the note they just replied to out of view.
      */}
      {replyTo ? (
        <ComposerModal
          patient={patient}
          replyingTo={replyTo}
          onSubmitComment={(body) => handleComment(replyTo.id, body)}
          onClose={() => setReplyTo(null)}
        />
      ) : null}
    </div>
  );
};

ProgressNotes.propTypes = {
  /** The loaded patient record; `patient.id` is the notes API's patientId. */
  patient: PropTypes.shape({
    id: PropTypes.number,
  }),
};

export default ProgressNotes;
